import { mapWith } from "@p0security/util/collections";

import type { ComponentMaturity } from "../integrations/shared.ts";
import type { DevEnv } from "../types/environment.ts";
import type {
  InstallContext,
  InstallSpec,
  ItemConfigOf,
  Option,
} from "./types.ts";

/** Returns false if a component has "dev" maturity and this is not a devLike environment */
export const isAvailableInEnvironment = (
  maturity: ComponentMaturity,
  environmentType: DevEnv["environmentType"]
) => maturity !== "dev" || environmentType === "devLike";

/** Returns all visible components */
export const visibleComponentEntries = (
  spec: InstallSpec,
  environmentType: DevEnv["environmentType"],
  flags?: Record<string, boolean>
) =>
  Object.entries(spec).filter(([key, component]) => {
    const { featureFlag, hidden, maturity, type } = component;
    return (
      !hidden &&
      type !== "generated" &&
      key !== "root" &&
      (!featureFlag || flags?.[featureFlag]) &&
      isAvailableInEnvironment(maturity, environmentType)
    );
  });

/** Returns all visible component keys */
export const visibleComponentKeys = (
  spec: InstallSpec,
  environmentType: DevEnv["environmentType"],
  flags?: Record<string, boolean>
) => visibleComponentEntries(spec, environmentType, flags).map(([key]) => key);

/** Returns the options of a select element that a user may choose */
export const visibleOptionEntries = <O extends Record<string, Option<any>>>(
  options: O,
  environmentType: DevEnv["environmentType"]
) =>
  Object.entries(options).filter(
    ([_key, { hidden, maturity }]) =>
      !hidden && isAvailableInEnvironment(maturity, environmentType)
  );

/** Returns all installed items of a component, for an install context */
export const otherInstalled =
  <R extends Record<string, Record<string, ItemConfigOf<any>>>>(
    component: keyof R
  ) =>
  async (context: InstallContext<R>) =>
    context.config
      ? mapWith(
          Object.entries(context.config[component] ?? {}),
          function* ([id, { state, label }]) {
            if (state === "installed") yield { id, label };
          }
        )
      : [];

/** Returns the item that corresponds to the current installation regardless of state */
export const currentInstall =
  <R extends Record<string, Record<string, ItemConfigOf<any>>>>(
    component: keyof R
  ) =>
  (context: InstallContext<R>, installId: string) =>
    context.config
      ? mapWith(
          Object.entries(context.config[component] ?? {}),
          function* ([id, { label }]) {
            if (id === installId) yield { id, label };
          }
        )
      : [];
